import React from "react";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { MainContext } from "../App";
import AudioManager from "../@shared/utils/AudioManager";
import {
  getChromePalette,
  getContentTextShadow,
  getCornerRadius,
  getWrapperFilter,
  normalizeButtonWidth,
  normalizeDimension,
  resolveThemeColor,
} from "./neoSvgUtils";

function useElementSize({ width: defaultWidth, height: defaultHeight }) {
  const ref = React.useRef(null);
  const [size, setSize] = React.useState({
    width: defaultWidth,
    height: defaultHeight,
  });

  React.useLayoutEffect(() => {
    const node = ref.current;

    if (!node) {
      return undefined;
    }

    const updateSize = () => {
      const nextWidth = Math.max(node.offsetWidth || defaultWidth, 1);
      const nextHeight = Math.max(node.offsetHeight || defaultHeight, 1);

      setSize((current) => {
        if (
          Math.abs(current.width - nextWidth) < 1 &&
          Math.abs(current.height - nextHeight) < 1
        ) {
          return current;
        }

        return { width: nextWidth, height: nextHeight };
      });
    };

    updateSize();

    const observer = new ResizeObserver(() => {
      updateSize();
    });

    observer.observe(node);

    return () => {
      observer.disconnect();
    };
  }, [defaultHeight, defaultWidth]);

  return [ref, size];
}

function buildHexPath(width, height, inset, slant) {
  const halfHeight = height / 2;
  const safeWidth = Math.max(width, (inset + slant) * 2 + 8);
  const safeHeight = Math.max(height, inset * 2 + 8);

  return [
    `M ${inset + slant} ${inset}`,
    `H ${safeWidth - inset - slant}`,
    `L ${safeWidth - inset} ${halfHeight}`,
    `L ${safeWidth - inset - slant} ${safeHeight - inset}`,
    `H ${inset + slant}`,
    `L ${inset} ${halfHeight}`,
    "Z",
  ].join(" ");
}

function buildTopGlossPath(width, height, inset, slant) {
  const upperBandBottom = Math.min(height * 0.42, height - inset - 6);
  const sideJoinY = Math.min(height * 0.53, height - inset - 4);

  return [
    `M ${inset + slant} ${inset}`,
    `H ${width - inset - slant}`,
    `L ${width - inset} ${height / 2}`,
    `L ${width - inset} ${sideJoinY}`,
    `L ${width - inset - slant} ${upperBandBottom}`,
    `H ${inset + slant}`,
    `L ${inset} ${sideJoinY}`,
    `L ${inset} ${height / 2}`,
    "Z",
  ].join(" ");
}

function buildBottomShadowPath(width, height, inset, slant) {
  const lowerBandTop = Math.max(height * 0.58, inset + 6);
  const sideJoinY = Math.max(height * 0.47, inset + 4);

  return [
    `M ${inset} ${sideJoinY}`,
    `L ${inset + slant} ${lowerBandTop}`,
    `H ${width - inset - slant}`,
    `L ${width - inset} ${sideJoinY}`,
    `L ${width - inset} ${height / 2}`,
    `L ${width - inset - slant} ${height - inset}`,
    `H ${inset + slant}`,
    `L ${inset} ${height / 2}`,
    "Z",
  ].join(" ");
}

function buildCornerCutPath(width, height, inset, cut) {
  return [
    `M ${inset + cut} ${inset}`,
    `H ${width - inset - cut}`,
    `L ${width - inset} ${inset + cut}`,
    `V ${height - inset - cut}`,
    `L ${width - inset - cut} ${height - inset}`,
    `H ${inset + cut}`,
    `L ${inset} ${height - inset - cut}`,
    `V ${inset + cut}`,
    "Z",
  ].join(" ");
}

function FantasyButtonFrame({
  idPrefix,
  color,
  disabled,
  isIcon,
  width,
  height,
}) {
  const palette = getChromePalette(color);
  const fillBase = disabled ? palette.disabledBase : palette.base;
  const fillTop = disabled ? palette.disabledBase : palette.fillTop;
  const fillBottom = disabled ? palette.fillShadow : palette.fillBottom;

  if (isIcon) {
    const frameWidth = Math.max(width || 50, 32);
    const frameHeight = Math.max(height || 50, 32);
    const outerInset = 1.5;
    const goldInset = 5;
    const innerInset = 9;
    const outerCut = Math.min(frameWidth, frameHeight) * 0.16;
    const goldCut = Math.min(frameWidth, frameHeight) * 0.145;
    const innerCut = Math.min(frameWidth, frameHeight) * 0.125;

    return (
      <svg
        viewBox={`0 0 ${frameWidth} ${frameHeight}`}
        xmlns="http://www.w3.org/2000/svg"
        preserveAspectRatio="none"
        width="100%"
        height="100%"
      >
        <defs>
          <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={palette.outerDark} />
            <stop offset="100%" stopColor="#09030f" />
          </linearGradient>
          <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.goldBright} />
            <stop offset="55%" stopColor={palette.goldMid} />
            <stop offset="100%" stopColor={palette.goldDark} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={fillTop} />
            <stop offset="55%" stopColor={fillBase} />
            <stop offset="100%" stopColor={fillBottom} />
          </linearGradient>
          <linearGradient id={`${idPrefix}-accent`} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={palette.topHighlight} />
            <stop offset="100%" stopColor={palette.accentGlow} />
          </linearGradient>
        </defs>

        <path
          d={buildCornerCutPath(frameWidth, frameHeight, outerInset, outerCut)}
          fill={`url(#${idPrefix}-outer)`}
        />
        <path
          d={buildCornerCutPath(frameWidth, frameHeight, goldInset, goldCut)}
          fill="none"
          stroke={`url(#${idPrefix}-gold)`}
          strokeWidth="3"
        />
        <path
          d={buildCornerCutPath(frameWidth, frameHeight, innerInset, innerCut)}
          fill={`url(#${idPrefix}-fill)`}
          stroke={palette.accent}
          strokeOpacity="0.8"
          strokeWidth="1.5"
        />
        <path
          d={buildTopGlossPath(frameWidth, frameHeight, innerInset, innerCut)}
          fill={`url(#${idPrefix}-accent)`}
          opacity="0.9"
        />
        <path
          d={buildBottomShadowPath(frameWidth, frameHeight, innerInset, innerCut)}
          fill={palette.edgeShadow}
          opacity="0.85"
        />
      </svg>
    );
  }

  const frameWidth = Math.max(width || 320, 120);
  const frameHeight = Math.max(height || 44, 36);
  const outerInset = 1.5;
  const goldInset = 5;
  const innerInset = 10;
  const outerSlant = Math.min(frameHeight * 0.4, 18);
  const goldSlant = Math.min(frameHeight * 0.34, 15);
  const innerSlant = Math.min(frameHeight * 0.3, 13);

  return (
    <svg
      viewBox={`0 0 ${frameWidth} ${frameHeight}`}
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="none"
      width="100%"
      height="100%"
    >
      <defs>
        <linearGradient id={`${idPrefix}-outer`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a0b2d" />
          <stop offset="100%" stopColor="#09030f" />
        </linearGradient>
        <linearGradient id={`${idPrefix}-gold`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor={palette.goldBright} />
          <stop offset="52%" stopColor={palette.goldMid} />
          <stop offset="100%" stopColor={palette.goldDark} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-fill`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={fillTop} />
          <stop offset="56%" stopColor={fillBase} />
          <stop offset="100%" stopColor={fillBottom} />
        </linearGradient>
        <linearGradient id={`${idPrefix}-gloss`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={palette.topHighlight} />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>

      <path
        d={buildHexPath(frameWidth, frameHeight, outerInset, outerSlant)}
        fill={`url(#${idPrefix}-outer)`}
      />
      <path
        d={buildHexPath(frameWidth, frameHeight, goldInset, goldSlant)}
        fill="none"
        stroke={`url(#${idPrefix}-gold)`}
        strokeWidth="3.2"
      />
      <path
        d={buildHexPath(frameWidth, frameHeight, innerInset, innerSlant)}
        fill={`url(#${idPrefix}-fill)`}
        stroke={palette.accent}
        strokeOpacity="0.78"
        strokeWidth="1.5"
      />
      <path
        d={buildTopGlossPath(frameWidth, frameHeight, innerInset, innerSlant)}
        fill={`url(#${idPrefix}-gloss)`}
        opacity="0.95"
      />
      <path
        d={buildBottomShadowPath(frameWidth, frameHeight, innerInset, innerSlant)}
        fill={palette.edgeShadow}
        opacity="0.95"
      />
      <path
        d={`M ${innerInset + innerSlant} ${innerInset + 3} H ${
          frameWidth - innerInset - innerSlant
        }`}
        stroke={palette.goldBright}
        strokeOpacity="0.72"
        strokeWidth="1.1"
      />
    </svg>
  );
}

export default function NeoButton({
  children,
  onClick,
  width,
  height,
  text,
  subText,
  color = "#0070f7",
  isIcon,
  icon,
  startAdorn,
  endAdorn,
  disabled,
  borderRadius,
  textSize,
  dataDriver,
  allowCompact = false,
}) {
  const { Theme } = React.useContext(MainContext);
  const buttonId = React.useId().replace(/:/g, "");
  const resolvedColor = resolveThemeColor(color, Theme, "#4f2cdf");
  const iconOnly = Boolean(isIcon);
  const motionProps = disabled
    ? {}
    : {
        whileHover: { scale: 1.02, y: -1 },
        whileTap: { scale: 0.985, y: 0 },
      };

  const handleClick = (event) => {
    if (disabled || !onClick) {
      return;
    }

    onClick(event);
    AudioManager.playSFX("buttonPress");
  };

  const iconSize = normalizeDimension(width, "50px");
  const iconHeight = normalizeDimension(height ?? width, iconSize);
  const textWidth = normalizeButtonWidth(width);
  const textHeight = normalizeDimension(height, "60px");
  const wrapperRadius = getCornerRadius(borderRadius, iconOnly ? 16 : 14);
  const textShadow = getContentTextShadow(disabled);
  const [frameRef, frameSize] = useElementSize({
    width: iconOnly ? 50 : 320,
    height: iconOnly ? 50 : 60,
  });

  return (
    <motion.div
      data-driver={dataDriver}
      {...motionProps}
      style={{
        display: "inline-block",
        width: iconOnly ? iconSize : textWidth,
        height: iconOnly ? iconHeight : textHeight,
        minHeight: iconOnly ? undefined : allowCompact ? textHeight : "60px",
        filter: getWrapperFilter(disabled),
      }}
    >
      <Box
        component="button"
        type="button"
        disabled={disabled}
        aria-label={text || subText || (iconOnly ? "button" : undefined)}
        onClick={handleClick}
        ref={frameRef}
        sx={{
          all: "unset",
          width: "100%",
          height: "100%",
          display: "block",
          position: "relative",
          containerType: textSize ? "inline-size" : undefined,
          minHeight: iconOnly ? undefined : allowCompact ? textHeight : "60px",
          cursor: disabled ? "default" : "pointer",
          pointerEvents: disabled ? "none" : "auto",
          borderRadius: `${wrapperRadius}px`,
          overflow: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            filter: disabled ? "none" : "drop-shadow(0 8px 14px rgba(0, 0, 0, 0.34))",
          }}
        >
          <FantasyButtonFrame
            idPrefix={`neo-button-${buttonId}`}
            color={resolvedColor}
            disabled={disabled}
            isIcon={iconOnly}
            width={frameSize.width}
            height={frameSize.height}
          />
        </Box>

        {iconOnly ? (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff3b3",
              textShadow,
              "& > *": {
                fontSize: "1.7rem",
              },
            }}
          >
            {icon || children}
          </Box>
        ) : (
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              width: "100%",
              height: "100%",
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)",
              alignItems: "center",
              gap: 1,
              px: subText ? 2.25 : 2,
              py: subText ? 0.75 : 0.5,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 0.75,
                color: "#fff3b3",
              }}
            >
              {startAdorn}
              {icon}
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                lineHeight: 1,
              }}
            >
              {(text || children) && (
                <Typography
                  component="span"
                  sx={{
                    color: "#ffe99f",
                    textAlign: "center",
                    fontWeight: 900,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    lineHeight: subText ? 0.98 : 1,
                    textShadow,
                    fontSize: textSize
                      ? `${textSize}cqw`
                      : subText
                        ? { xs: "0.88rem", md: "0.98rem" }
                        : { xs: "0.92rem", md: "1rem" },
                    maxWidth: "100%",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {text || children}
                </Typography>
              )}

              {subText && (
                <Typography
                  component="span"
                  sx={{
                    mt: 0.45,
                    maxWidth: "100%",
                    color: "#fff6d6",
                    textAlign: "center",
                    fontSize: { xs: "0.5rem", md: "0.58rem" },
                    lineHeight: 1.1,
                    textShadow,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {subText}
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                minWidth: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                gap: 0.75,
                color: "#fff3b3",
              }}
            >
              {endAdorn}
            </Box>
          </Box>
        )}
      </Box>
    </motion.div>
  );
}
